import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Trash2, Edit, Mail, Phone, Building2 } from "lucide-react";

const CATS = ["forestry","renewable","ocean","agriculture","direct_air"];
const empty = { name:"", category:"forestry", location:"", description:"", price_per_ton:"", available_tons:"", image_url:"", certification:"", project_developer:"", year: new Date().getFullYear() };

const STATUSES = [
  { key: "new", label: "New", color: "bg-blue-100 text-blue-800" },
  { key: "contacted", label: "Contacted", color: "bg-amber-100 text-amber-800" },
  { key: "quoted", label: "Quoted", color: "bg-purple-100 text-purple-800" },
  { key: "won", label: "Won", color: "bg-emerald-100 text-emerald-800" },
  { key: "lost", label: "Lost", color: "bg-stone-100 text-stone-600" },
];

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [projects, setProjects] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [quoteFilter, setQuoteFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/projects").then((r) => setProjects(r.data));
    api.get("/admin/enterprise-quotes").then((r) => setQuotes(r.data));
  };

  useEffect(load, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...p }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, price_per_ton: Number(form.price_per_ton), available_tons: Number(form.available_tons), year: form.year ? Number(form.year) : null };
    try {
      if (editing) await api.put(`/projects/${editing.id}`, payload);
      else await api.post("/projects", payload);
      toast.success(editing ? "Project updated" : "Project created");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this project?")) return;
    await api.delete(`/projects/${id}`);
    toast.success("Deleted");
    load();
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/enterprise-quotes/${id}`, { status });
      setQuotes((prev) => prev.map((q) => q.id === id ? { ...q, status } : q));
      toast.success("Status updated");
    } catch { toast.error("Update failed"); }
  };

  const setF = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const filteredQuotes = quoteFilter === "all" ? quotes : quotes.filter((q) => q.status === quoteFilter);
  const quoteCounts = STATUSES.reduce((acc, s) => ({ ...acc, [s.key]: quotes.filter(q => q.status === s.key).length }), { all: quotes.length });

  return (
    <div className="max-w-7xl mx-auto px-6 py-10">
      <div className="flex justify-between items-end mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-stone-500">Admin</div>
          <h1 className="text-4xl tracking-tighter font-light mt-2">Operations</h1>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
          {[
            ["Users", stats.users], ["Projects", stats.projects], ["Orders", stats.orders],
            ["Tons sold", stats.total_tons.toLocaleString()], ["Revenue", `$${stats.total_revenue.toLocaleString()}`],
          ].map(([k,v]) => (
            <div key={k} className="bg-white border border-stone-200 rounded-2xl p-5">
              <div className="text-xs uppercase tracking-widest text-stone-500">{k}</div>
              <div className="text-2xl font-light mt-1">{v}</div>
            </div>
          ))}
        </div>
      )}

      <Tabs defaultValue="projects">
        <TabsList className="bg-white border border-stone-200 rounded-full h-11 p-1">
          <TabsTrigger value="projects" data-testid="tab-projects" className="rounded-full px-5">Projects</TabsTrigger>
          <TabsTrigger value="quotes" data-testid="tab-quotes" className="rounded-full px-5">
            Enterprise Quotes {quoteCounts.new > 0 && <span className="ml-2 bg-[#D97757] text-white text-[10px] rounded-full px-1.5">{quoteCounts.new}</span>}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="projects" className="mt-6">
          <div className="flex justify-end mb-4">
            <Button onClick={openNew} data-testid="admin-new-project" className="bg-[#2C5530] hover:bg-[#1F3D22] text-white rounded-full">
              <Plus className="w-4 h-4 mr-1"/> New project
            </Button>
          </div>
          <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
            <div className="p-6 border-b border-stone-200 text-sm font-medium">Projects</div>
            <div className="divide-y divide-stone-200">
              {projects.map((p) => (
                <div key={p.id} data-testid={`admin-project-${p.id}`} className="p-5 flex items-center gap-4">
                  <img src={p.image_url} alt="" className="w-16 h-16 rounded-xl object-cover"/>
                  <div className="flex-1">
                    <div className="font-medium">{p.name}</div>
                    <div className="text-sm text-stone-500">{p.category} · {p.location} · ${p.price_per_ton}/t · {p.available_tons} t left</div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => openEdit(p)} data-testid={`edit-${p.id}`}><Edit className="w-4 h-4"/></Button>
                  <Button variant="ghost" size="sm" className="text-red-600" onClick={() => remove(p.id)} data-testid={`del-${p.id}`}><Trash2 className="w-4 h-4"/></Button>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="quotes" className="mt-6">
          <div className="flex flex-wrap gap-2 mb-4">
            <button onClick={() => setQuoteFilter("all")} data-testid="qf-all" className={`px-4 py-1.5 rounded-full text-sm border ${quoteFilter === "all" ? "bg-[#2C5530] text-white border-[#2C5530]" : "bg-white border-stone-200"}`}>
              All · {quoteCounts.all}
            </button>
            {STATUSES.map((s) => (
              <button key={s.key} data-testid={`qf-${s.key}`} onClick={() => setQuoteFilter(s.key)} className={`px-4 py-1.5 rounded-full text-sm border ${quoteFilter === s.key ? "bg-[#2C5530] text-white border-[#2C5530]" : "bg-white border-stone-200"}`}>
                {s.label} · {quoteCounts[s.key] || 0}
              </button>
            ))}
          </div>

          {filteredQuotes.length === 0 ? (
            <div className="bg-white border border-dashed border-stone-300 rounded-2xl p-12 text-center text-stone-500">
              No enterprise quotes {quoteFilter !== "all" ? `with status "${quoteFilter}"` : "yet"}.
            </div>
          ) : (
            <div className="space-y-3">
              {filteredQuotes.map((q) => {
                const status = STATUSES.find((s) => s.key === q.status) || STATUSES[0];
                return (
                  <div key={q.id} data-testid={`quote-${q.id}`} className="bg-white border border-stone-200 rounded-2xl p-5">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl bg-[#2C5530]/10 flex items-center justify-center flex-shrink-0">
                          <Building2 className="w-5 h-5 text-[#2C5530]"/>
                        </div>
                        <div>
                          <div className="font-medium text-lg">{q.company_name}</div>
                          <div className="text-sm text-stone-500 mt-0.5">{q.contact_name} · {q.industry || "—"} · {q.country || "—"}</div>
                          <div className="flex flex-wrap gap-3 mt-2 text-xs text-stone-600">
                            <span className="flex items-center gap-1"><Mail className="w-3 h-3"/>{q.email}</span>
                            {q.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3"/>{q.phone}</span>}
                            <span>Submitted {new Date(q.created_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-3xl font-light tracking-tight text-[#2C5530]">{q.tons_needed.toLocaleString()}</div>
                        <div className="text-xs uppercase tracking-widest text-stone-500">tons/year</div>
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-stone-100 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <div className="text-xs uppercase tracking-widest text-stone-500 mb-1">Timeline</div>
                        <div>{q.timeline || "—"}</div>
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-widest text-stone-500 mb-1">Categories</div>
                        <div className="flex flex-wrap gap-1">
                          {(q.preferred_categories || []).length === 0 && <span className="text-stone-500">any</span>}
                          {(q.preferred_categories || []).map((c) => <Badge key={c} variant="outline" className="capitalize border-stone-200">{c.replace("_"," ")}</Badge>)}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-widest text-stone-500 mb-1">Notes</div>
                        <div className="text-stone-600 line-clamp-2">{q.notes || "—"}</div>
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-stone-100 flex items-center justify-between gap-3">
                      <div className={`px-3 py-1 rounded-full text-xs font-medium ${status.color}`}>{status.label}</div>
                      <div className="flex gap-2 items-center">
                        <a href={`mailto:${q.email}`} className="text-xs text-[#2C5530] hover:underline">Reply</a>
                        <Select value={q.status} onValueChange={(v) => updateStatus(q.id, v)}>
                          <SelectTrigger data-testid={`status-${q.id}`} className="h-9 w-36 bg-stone-50 border-stone-200 text-sm"><SelectValue/></SelectTrigger>
                          <SelectContent className="bg-white">
                            {STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit project" : "Create project"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Name</Label>
                <Input required value={form.name} onChange={setF("name")} className="mt-1"/>
              </div>
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={setF("category")}>
                  <SelectTrigger className="mt-1"><SelectValue/></SelectTrigger>
                  <SelectContent className="bg-white">{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Location</Label>
                <Input required value={form.location} onChange={setF("location")} className="mt-1"/>
              </div>
              <div className="col-span-2">
                <Label>Description</Label>
                <Textarea required value={form.description} onChange={setF("description")} className="mt-1" rows={3}/>
              </div>
              <div>
                <Label>Price per ton (USD)</Label>
                <Input required type="number" step="0.01" value={form.price_per_ton} onChange={setF("price_per_ton")} className="mt-1"/>
              </div>
              <div>
                <Label>Available tons</Label>
                <Input required type="number" value={form.available_tons} onChange={setF("available_tons")} className="mt-1"/>
              </div>
              <div className="col-span-2">
                <Label>Image URL</Label>
                <Input required value={form.image_url} onChange={setF("image_url")} className="mt-1"/>
              </div>
              <div>
                <Label>Certification</Label>
                <Input required value={form.certification} onChange={setF("certification")} className="mt-1"/>
              </div>
              <div>
                <Label>Developer</Label>
                <Input value={form.project_developer || ""} onChange={setF("project_developer")} className="mt-1"/>
              </div>
              <div>
                <Label>Year</Label>
                <Input type="number" value={form.year || ""} onChange={setF("year")} className="mt-1"/>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#2C5530] hover:bg-[#1F3D22]">{editing ? "Save" : "Create"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
